import React, { useState } from 'react';
import { Plus, Edit2, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Category } from '../types';

interface CategorySidebarProps {
  categories: Category[];
  selectedCategoryId: string | null;
  onSelectCategory: (categoryId: string | null) => void;
  onAddCategory: (name: string) => void;
  onEditCategory: (categoryId: string, name: string) => void;
  onDeleteCategory: (categoryId: string) => void;
  onMoveCategory: (categoryId: string, direction: 'up' | 'down') => void;
  noteCountByCategory: Record<string, number>;
}

export const CategorySidebar: React.FC<CategorySidebarProps> = ({
  categories,
  selectedCategoryId,
  onSelectCategory,
  onAddCategory,
  onEditCategory,
  onDeleteCategory,
  onMoveCategory,
  noteCountByCategory
}) => {
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');

  const handleAddCategory = () => {
    if (newCategoryName.trim()) {
      onAddCategory(newCategoryName.trim());
      setNewCategoryName('');
      setIsAddingCategory(false);
    }
  };

  const handleEditCategory = (categoryId: string) => {
    if (editingCategoryName.trim()) {
      onEditCategory(categoryId, editingCategoryName.trim());
      setEditingCategoryId(null);
      setEditingCategoryName('');
    }
  };

  const startEditCategory = (category: Category) => {
    setEditingCategoryId(category.id);
    setEditingCategoryName(category.name);
  };

  const cancelEdit = () => {
    setEditingCategoryId(null);
    setEditingCategoryName('');
  };

  const totalNotes = Object.values(noteCountByCategory).reduce((sum, count) => sum + count, 0);

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 h-fit sticky top-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-800">Categories</h2>
        <button
          onClick={() => setIsAddingCategory(true)}
          className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
          title="Add category"
        >
          <Plus size={20} />
        </button>
      </div>

      {isAddingCategory && (
        <div className="mb-3 p-2 bg-blue-50 rounded-lg">
          <input
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
            placeholder="Category name..."
            className="w-full px-2 py-1 border border-blue-300 rounded mb-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={handleAddCategory}
              className="flex-1 px-2 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
            >
              Add
            </button>
            <button
              onClick={() => {
                setIsAddingCategory(false);
                setNewCategoryName('');
              }}
              className="flex-1 px-2 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-1">
        <button
          onClick={() => onSelectCategory(null)}
          className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
            selectedCategoryId === null
              ? 'bg-blue-100 text-blue-700 font-semibold'
              : 'hover:bg-gray-100 text-gray-700'
          }`}
        >
          <span>All references</span>
          <span className="text-sm text-gray-500">({totalNotes})</span>
        </button>

        {categories.map((category, index) => (
          <div key={category.id}>
            {editingCategoryId === category.id ? (
              <div className="p-2 bg-blue-50 rounded-lg">
                <input
                  type="text"
                  value={editingCategoryName}
                  onChange={(e) => setEditingCategoryName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleEditCategory(category.id)}
                  className="w-full px-2 py-1 border border-blue-300 rounded mb-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditCategory(category.id)}
                    className="flex-1 px-2 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="flex-1 px-2 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div
                className={`group flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                  selectedCategoryId === category.id
                    ? 'bg-blue-100 text-blue-700 font-semibold'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <button
                  onClick={() => onSelectCategory(category.id)}
                  className="flex-1 text-left flex items-center justify-between"
                >
                  <span>{category.name}</span>
                  <span className="text-sm text-gray-500">
                    ({noteCountByCategory[category.id] || 0})
                  </span>
                </button>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                  <button
                    onClick={() => onMoveCategory(category.id, 'up')}
                    disabled={index === 0}
                    className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    onClick={() => onMoveCategory(category.id, 'down')}
                    disabled={index === categories.length - 1}
                    className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <ChevronDown size={14} />
                  </button>
                  <button
                    onClick={() => startEditCategory(category)}
                    className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Edit category"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete category "${category.name}"? Notes in this category will not be deleted.`)) {
                        onDeleteCategory(category.id);
                      }
                    }}
                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete category"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {categories.length === 0 && !isAddingCategory && (
        <p className="text-sm text-gray-500 text-center py-4">
          No categories yet. Click + to add one.
        </p>
      )}
    </div>
  );
};
